import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import '../backlog/backlog.css';
import './resultEntryFormula.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar, ExportMenu } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { useConfirm, useSaveFlow, useDeleteFlow } from '../../components/confirm';
import { useNotify } from '../../components/notifications';
import { fmtLocalDateTime } from '../../utils/datetime';
import { useNavPermission } from '../../hooks/useNavPermission';
import { useModalKeys } from '../../hooks/useModalKeys';

/**
 * Settings ▸ SDMS ▸ SDMS Formula Setup — the SDMS TEMPLATE calculation
 * ASSIGNMENT (dbo.SpreadSheetBuilder_TemplateFormula, legacy/shared table),
 * keyed by TemplateID (dbo.SpreadSheetBuilder_TemplateInfo) — the SDMS
 * parallel to Result Entry Express Formula Setup (same shared Formula
 * Library, different assignment table/key). Settings-only — SDMS template
 * execution is a separate, pre-existing subsystem this form does not touch.
 *
 * Follows the SAME structure/action-bar position/validation/audit pattern as
 * every other Settings form (§18.6/§18.8): shared FormTitle (title + load-time
 * + audit chip), shared AppToolbar (canonical New/Edit/Delete/Refresh/Export
 * order), shared useSaveFlow/useDeleteFlow ("Do you want to Save?" → save →
 * "Saved successfully." / danger-confirm → "Deleted."), server-authoritative
 * validation + 403 on missing Write, and audit via IAuditTrailService
 * (Create/Update/Delete). Exceptions are captured automatically by the global
 * ExceptionHandlingMiddleware into dbo.AI_ExceptionLog — no per-form code needed.
 */

interface MapRow {
  test: string | null; method: string | null; templateID: number; templateName: string | null;
  templateType: string | null; formulaCount: number; modifiedAt: string | null; modifiedBy: string | null;
}
interface LibRow { formulaUqId: number; formulaID: string | null; formulaName: string; kind: number; targetField: string | null; series: number }
interface TemplateOpt { templateID: number; templateName: string | null; test: string | null }
interface DetailRow { formulaUqId: number; executionOrder: number }

export default function SdmsFormulaPage({ onHome }: { onHome?: () => void }) {
  const { alert } = useConfirm();
  const notify = useNotify();
  const runSave = useSaveFlow();
  const runDelete = useDeleteFlow();
  const perm = useNavPermission('SDMS');

  const [maps, setMaps] = useState<MapRow[]>([]);
  const [lib, setLib] = useState<LibRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [editor, setEditor] = useState<{ template: TemplateOpt | null; picked: Map<number, number>; viewOnly: boolean } | null>(null);
  const [templates, setTemplates] = useState<TemplateOpt[]>([]);
  const [templateSearch, setTemplateSearch] = useState('');
  const [fullGrid, setFullGrid] = useState(false);   // page-level grid maximize (search-row zoom button)

  const load = useCallback(async () => {
    setLoading(true);
    const t0 = performance.now();
    try {
      const [m, l] = await Promise.all([
        api.get('/api/v1/sdms-formula/mappings', { silentLoading: true } as never),
        api.get('/api/v1/sdms-formula/library', { silentLoading: true } as never),
      ]);
      setMaps((m.data as { data: MapRow[] }).data ?? []);
      setLib((l.data as { data: LibRow[] }).data ?? []);
    } catch {
      notify.error('Failed to load SDMS Formula mappings.');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { void load(); }, [load]);

  // Esc restores the maximized grid (search-row Maximize button).
  useEffect(() => {
    if (!fullGrid) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setFullGrid(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullGrid]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return maps;
    return maps.filter(m => [m.templateName, m.test, m.method, m.templateType].some(v => (v ?? '').toLowerCase().includes(q)));
  }, [maps, search]);

  const openEditor = useCallback(async (existing?: MapRow, viewOnly = false) => {
    try {
      if (!viewOnly && templates.length === 0) {
        const r = await api.get('/api/v1/sdms-formula/templates');
        setTemplates((r.data as { data: TemplateOpt[] }).data ?? []);
      }
      const picked = new Map<number, number>();
      let template: TemplateOpt | null = null;
      if (existing) {
        template = { templateID: existing.templateID, templateName: existing.templateName, test: existing.test };
        const d = await api.get(`/api/v1/sdms-formula/mappings/${existing.templateID}`);
        for (const row of ((d.data as { data: DetailRow[] }).data ?? [])) picked.set(row.formulaUqId, row.executionOrder);
      }
      setTemplateSearch('');
      setEditor({ template, picked, viewOnly });
    } catch {
      notify.error('Could not open the formula editor.');
    }
  }, [templates.length, notify]);

  const saveEditor = useCallback(async () => {
    if (!editor?.template) { void alert({ title: 'SDMS Formula Setup', message: 'Select a Template first.', tone: 'warning' }); return; }
    if (editor.picked.size === 0) { void alert({ title: 'SDMS Formula Setup', message: 'Pick at least one formula.', tone: 'warning' }); return; }
    const isNew = !maps.some(m => m.templateID === editor.template!.templateID);
    const formulas = [...editor.picked.entries()]
      .sort((a, b) => a[1] - b[1])
      .map(([formulaUqId, executionOrder]) => ({ formulaUqId, executionOrder }));

    await runSave({
      formName: 'SDMS Formula Setup',
      auditAction: isNew ? 'create' : 'edit',
      save: async () => {
        await api.put(`/api/v1/sdms-formula/mappings/${editor.template!.templateID}`, {
          templateID: editor.template!.templateID,
          formulas,
        });
      },
      onSaved: () => { setEditor(null); void load(); },
    });
  }, [editor, maps, runSave, load, alert]);

  const deleteSelected = useCallback(async () => {
    const rows = maps.filter(m => selected.has(m.templateID));
    if (rows.length === 0) { void alert({ title: 'SDMS Formula Setup', message: 'Select the mapping(s) to delete first.', tone: 'warning' }); return; }
    await runDelete({
      formName: 'SDMS Formula Setup',
      message: `Delete ${rows.length} mapping${rows.length === 1 ? '' : 's'}?`,
      delete: async () => { for (const r of rows) await api.delete(`/api/v1/sdms-formula/mappings/${r.templateID}`); },
      onDeleted: () => { setSelected(new Set()); void load(); },
    });
  }, [maps, selected, runDelete, load, alert]);

  // Per-row Edit pencil next to the checkbox (owner request 2026-10-13, image 496) — opens the
  // record in edit mode, like Formula Library. Only when the user can write (mirrors toolbar Edit).
  const editCol: AppColumn<MapRow> = {
    field: 'edit', header: '', width: 40, sortable: false, filterable: false, noExport: true,
    render: r => (
      <button className="ref-view-btn" title="Edit" aria-label="Edit" style={{ color: '#1F4E79' }}
        onClick={e => { e.preventDefault(); e.stopPropagation(); void openEditor(r); }}>
        <i className="ti ti-edit" style={{ fontSize: 16 }} />
      </button>
    ),
  };
  const mapCols: AppColumn<MapRow>[] = [
    ...(perm.canWrite ? [editCol] : []),
    { field: 'test',         header: 'Test',            width: 180, text: r => r.test ?? '' },
    { field: 'method',       header: 'Method',          width: 160, text: r => r.method ?? '' },
    { field: 'templateID',   header: 'TemplateID',      width: 110, text: r => String(r.templateID) },
    { field: 'templateName', header: 'TemplateName',    width: 220, text: r => r.templateName ?? '' },
    { field: 'templateType', header: 'TemplateType',    width: 140, text: r => r.templateType ?? '' },
    { field: 'formulaCount', header: 'No.ofFormulas',   width: 130, text: r => String(r.formulaCount) },
    { field: 'modifiedAt',   header: 'Modified',        width: 150, text: r => r.modifiedAt ? fmtLocalDateTime(r.modifiedAt) : '' },
    { field: 'modifiedBy',   header: 'Modified By',     width: 160, text: r => r.modifiedBy ?? '' },
    {
      field: 'view', header: 'View', width: 70, sortable: false, filterable: false, noExport: true,
      render: r => (
        <button className="ref-view-btn" title="View assigned formulas" aria-label="View"
          onClick={e => { e.preventDefault(); e.stopPropagation(); void openEditor(r, true); }}>
          <i className="ti ti-eye" style={{ fontSize: 16 }} />
        </button>
      ),
    },
  ];

  const templateMatches = useMemo(() => {
    const q = templateSearch.trim().toLowerCase();
    if (!q) return templates.slice(0, 50);
    return templates.filter(t =>
      (t.templateName ?? '').toLowerCase().includes(q) ||
      (t.test ?? '').toLowerCase().includes(q)).slice(0, 50);
  }, [templates, templateSearch]);

  const selectedRow = maps.find(m => selected.has(m.templateID));

  // Bespoke popup keyboard: Esc closes (standing rule 14).
  const modalKeys = useModalKeys(!!editor, { onClose: () => setEditor(null) });

  return (
    <div className={`bl-page${fullGrid ? ' re-fullgrid' : ''}`}>
      <div className="bl-top">
        {/* Nudge the breadcrumb down +10px and tighten below −10px (owner request 2026-10-16). */}
        <div className="dp-breadcrumb" style={{ marginTop: 10, marginBottom: -4 }}>
          <span className="dp-bc-link" onClick={onHome}>Home</span>
          <i className="ti ti-chevron-right" />
          <span>Settings</span>
          <i className="ti ti-chevron-right" />
          <span>SDMS</span>
          <i className="ti ti-chevron-right" />
          <span className="dp-bc-active">SDMS Formula Setup</span>
        </div>
        <FormTitle pageId="sdmsformula" title="SDMS Formula Setup" auditFormName="SDMS Formula Setup"
          loadMs={loadMs} loading={loading} />
      </div>

      <div className="bl-grid-area">
        <div className="re-max-hide" style={{ margin: '0 0 8px' }}>
          <AppToolbar
            onNew={perm.canWrite ? () => void openEditor() : undefined}
            newLabel="Add Formulas"
            onEdit={perm.canWrite ? () => {
              if (!selectedRow) { void alert({ title: 'SDMS Formula Setup', message: 'Select a mapping to edit.', tone: 'warning' }); return; }
              void openEditor(selectedRow);
            } : undefined}
            onDelete={perm.canWrite ? () => void deleteSelected() : undefined}
            onRefresh={load}
            refreshing={loading}
            exportData={{ filename: 'sdms-formula', ...gridExportMatrix(filtered, mapCols) }}
          />
        </div>

        {/* Search + Export + Maximize on ONE row OUTSIDE the grid card (owner request 2026-10-13,
            images 498/499 — matching Formula Library). The grid's own header tools are hidden, so
            the Export + Zoom controls are NOT a band inside the grid card. Maximize is a page-level
            flex fill (re-fullgrid on .bl-page), so this row stays visible when zoomed. */}
        <div className="list-search-row" style={fullGrid ? { padding: '4px 0 7px' } : undefined}>
          <div className="list-search">
            <span className="tb-i">🔍</span>
            <input type="text" placeholder="Search template, test…"
              value={search} onChange={e => setSearch(e.target.value)} />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
          </div>
          <div className="ref-grid-acts">
            <ExportMenu iconOnly triggerClassName="adg-max-btn"
              filename="sdms-formula" {...gridExportMatrix(filtered, mapCols)} />
            <button type="button" className="adg-max-btn" onClick={() => setFullGrid(v => !v)}
              title={fullGrid ? 'Restore (Esc)' : 'Maximize'} aria-label={fullGrid ? 'Restore' : 'Maximize'}>
              <i className={`ti ti-arrows-${fullGrid ? 'minimize' : 'maximize'}`} />
            </button>
          </div>
        </div>

        <div className="bl-card bl-card-flex">
          <AppDataGrid<MapRow>
            rows={filtered} columns={mapCols} getRowId={r => r.templateID}
            selectable selectedIds={selected} onSelectionChange={setSelected}
            loading={loading} loadMs={loadMs}
            hideHeaderTools
            emptyText="No formula mappings yet — click Add Formulas."
          />
        </div>
      </div>

      {/* Add/Edit/View Formulas — pick a Template, tick formulas + order. */}
      {editor && (
        <div className="ref-overlay" onClick={() => setEditor(null)}>
          <div className="ref-dialog" onClick={e => e.stopPropagation()} {...modalKeys}>
            <div className="ref-dialog-hdr">
              <span><i className="ti ti-math-function" /> {editor.viewOnly ? 'View Formulas' : editor.template ? 'Edit Formulas' : 'Add Formulas'}</span>
              <i className="ti ti-x ref-dialog-x" onClick={() => setEditor(null)} />
            </div>

            <div className="ref-dialog-body">
              {!editor.template ? (
                <>
                  <input autoFocus className="ref-combo-search" placeholder="Search Template / Test…"
                    value={templateSearch} onChange={e => setTemplateSearch(e.target.value)} />
                  <div className="ref-combo-list">
                    {templateMatches.map(t => (
                      <div key={t.templateID} className="ref-combo-row"
                        onClick={() => setEditor(ed => ed && ({ ...ed, template: t }))}>
                        <b>{t.templateName}</b> · {t.test}
                      </div>
                    ))}
                    {templateMatches.length === 0 && <div className="ref-combo-empty">No matches.</div>}
                  </div>
                </>
              ) : (
                <div className="ref-combo-current">
                  <b>{editor.template.templateName}</b> · {editor.template.test}
                  {!editor.viewOnly && !maps.some(m => m.templateID === editor.template!.templateID) && (
                    <span className="ref-combo-change" onClick={() => setEditor(ed => ed && ({ ...ed, template: null }))}>change</span>
                  )}
                </div>
              )}

              <table className="ref-formula-table">
                <thead>
                  <tr>
                    {!editor.viewOnly && <th style={{ width: 34 }} />}
                    <th>Formula</th>
                    <th>TargetField</th>
                    <th style={{ width: 90 }}>Order</th>
                  </tr>
                </thead>
                <tbody>
                  {(editor.viewOnly
                    ? lib.filter(f => editor.picked.has(f.formulaUqId))
                        .sort((a, b) => (editor.picked.get(a.formulaUqId) ?? 0) - (editor.picked.get(b.formulaUqId) ?? 0))
                    : lib
                  ).map(f => {
                    const on = editor.picked.has(f.formulaUqId);
                    return (
                      <tr key={f.formulaUqId}>
                        {!editor.viewOnly && (
                          <td className="ref-chk">
                            <input type="checkbox" checked={on} onChange={e => setEditor(ed => {
                              if (!ed) return ed;
                              const picked = new Map(ed.picked);
                              if (e.target.checked) picked.set(f.formulaUqId, f.series); else picked.delete(f.formulaUqId);
                              return { ...ed, picked };
                            })} />
                          </td>
                        )}
                        <td>{f.formulaName}
                          {f.kind === 1 && <span className="ref-script-tag">(Script)</span>}</td>
                        <td>{f.targetField}</td>
                        <td className="ref-order">
                          {on && (
                            <input type="number" className="ref-order-input" value={editor.picked.get(f.formulaUqId)} min={1}
                              disabled={editor.viewOnly}
                              onChange={e => setEditor(ed => {
                                if (!ed) return ed;
                                const picked = new Map(ed.picked);
                                picked.set(f.formulaUqId, Number(e.target.value) || f.series);
                                return { ...ed, picked };
                              })} />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Item count on the LEFT, Save next to Cancel on the RIGHT (owner request
                2026-10-16) — .ref-dialog-ftr is space-between, so it lays out itself. */}
            <div className="ref-dialog-ftr">
              <span className="ref-ftr-count">{editor.picked.size} selected</span>
              <div className="ref-ftr-btns">
                {editor.viewOnly ? (
                  <button className="ca-btn" onClick={() => setEditor(null)}>Close</button>
                ) : (
                  <>
                    <button className="ca-btn ca-btn-solid" onClick={() => void saveEditor()}>Save</button>
                    <button className="ca-btn" onClick={() => setEditor(null)}>Cancel</button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
